import { useQueries } from '@tanstack/react-query';
import { supabase } from '../utils/supabaseClient';
import { useQueryError } from './useQueryError';

const TRENDING_QUERY_KEY = ['cards', 'trending'];
const JUST_IN_QUERY_KEY = ['cards', 'justIn'];

const fetchTrendingCards = async () => {
  const { data, error } = await supabase
    .from("cards")
    .select(`
      id, 
      logoimage, 
      companyname, 
      rank
    `)
    .order("rank", { ascending: false })
    .limit(6);

  if (error) throw error;
  return data?.slice(1, 6) || [];
};

const fetchJustInCards = async () => {
  const { data, error } = await supabase
    .from("cards")
    .select(`
      id, 
      logoimage, 
      companyname, 
      addedtime
    `)
    .order("addedtime", { ascending: false })
    .limit(5);

  if (error) throw error;
  return data || [];
};

export function useDiscoverSection() {
  const { handleError } = useQueryError();

  const results = useQueries({
    queries: [
      {
        queryKey: TRENDING_QUERY_KEY,
        queryFn: fetchTrendingCards,
        onError: handleError,
        staleTime: 1000 * 60 * 2, // 2 minutos
        cacheTime: 1000 * 60 * 5, // 5 minutos
      },
      {
        queryKey: JUST_IN_QUERY_KEY,
        queryFn: fetchJustInCards,
        onError: handleError,
        staleTime: 1000 * 60 * 1, // 1 minuto
        cacheTime: 1000 * 60 * 3, // 3 minutos
      }
    ]
  });

  const [trendingQuery, justInQuery] = results;

  const formatAddedTime = (addedtime) => {
    if (!addedtime) return "";
    const now = new Date();
    const addedDate = new Date(addedtime);
    const diffTime = Math.abs(now.getTime() - addedDate.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);

    if (diffDays < 1) return "Added today";
    if (diffDays === 1) return "Added yesterday";
    if (diffDays < 7) return `Added ${diffDays} days ago`;
    if (diffWeeks === 1) return "Added 1 week ago";
    if (diffWeeks < 4) return `Added ${diffWeeks} weeks ago`;

    return `Added ${addedDate.toLocaleDateString()}`;
  };

  const processData = (data) => {
    if (!data) return [];
    return data.map(card => ({
      ...card,
      formattedAddedTime: formatAddedTime(card.addedtime)
    }));
  };

  return {
    trendingCards: processData(trendingQuery.data),
    justInCards: processData(justInQuery.data),
    isLoading: results.some(query => query.isLoading),
    error: results.find(query => query.error)?.error,
    isTrendingLoading: trendingQuery.isLoading,
    isJustInLoading: justInQuery.isLoading,
    isFetching: results.some(query => query.isFetching),
    isRefetching: results.some(query => query.isRefetching)
  };
}